import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { LoadingCards } from '@eventbrite/eds-event-card';
import { EventCard } from '@eventbrite/event-renderer';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import { EventSearchBody } from '../../../../constants/search';
import { CategoryBrowseBucket } from '../../components/CategoryBrowseBucket';
import { CategoryBrowseEmptyState } from '../../components/CategoryBrowseEmptyState';
import { useEnvContext } from '../../context';
import { useBaseEventsQueryEvents } from '../../hooks/queries/events';
import { useLocation } from '../../hooks/queries/location';
import { CategoryBrowseViewMore } from '../CategoryBrowseViewMore';
import './CategoryBrowseEvents.scss';

const DEFAULT_TITLE = gettext('Most popular events');

interface CategoryBrowseEventsLoadingStateProps {
    renderLoading: boolean;
    cardCount: number;
    cardStyle: 'list' | 'grid';
}

const CategoryBrowseEventsLoadingState: React.FunctionComponent<
    CategoryBrowseEventsLoadingStateProps
> = (props) => {
    return props.renderLoading ? (
        <ul className="category-browse-page__events category-browse-page__events--loading">
            {[...Array(props.cardCount).keys()].map((item: number) => (
                <LoadingCards
                    cardCount={1}
                    key={item}
                    style={props.cardStyle}
                    dynamicWidth={true}
                />
            ))}
        </ul>
    ) : null;
};

interface CategoryBrowseEventsProps {
    queryOverload?: EventSearchBody;
    renderLoading?: boolean;
    renderEmptyState?: boolean;
    refetchOnMount?: boolean;
    lazyLoadCarouselElements?: boolean;
    title?: string | GenericLazyString;
    forceRenderHeader?: boolean;
}

export const CategoryBrowseEvents: React.FunctionComponent<
    CategoryBrowseEventsProps
> = (props) => {
    //Get context data
    const { user, env } = useEnvContext();
    const isAuthenticated = !!user?.isAuthenticated;
    const currencyFormat = env?.currencyFormat;

    //Fetch necessary data
    const { isLoading: isLocationLoading } = useLocation();
    const { data: events, isLoading } = useBaseEventsQueryEvents({
        queryOverload: props.queryOverload,
        //For the base bucket we want to ensure it is
        //enabled immediately and refetches on mount
        refetchOnMount: !props.queryOverload,
        optOutDedupeEvents: !props.queryOverload,
    });

    //Loading state
    const loading = isLocationLoading || isLoading;
    if (!props.forceRenderHeader && loading) {
        return (
            <CategoryBrowseEventsLoadingState
                renderLoading={!!props.renderLoading}
                cardCount={4}
                cardStyle={'grid'}
            />
        );
    }

    //Empty state
    const currentBucketHasContent = events && events.length > 1;
    const bucketTitle = props.title || DEFAULT_TITLE;

    //Handle case where no events returned or too few
    if (!props.forceRenderHeader && !currentBucketHasContent) {
        //Only one bucket has `renderLoading` set to true
        if (!props.renderEmptyState) return null;
        return <CategoryBrowseEmptyState />;
    }

    //Return event bucket
    return (
        <CategoryBrowseBucket>
            <SimpleCarousel
                slidesVisible={{ desktop: 4, tablet: 2 }}
                headerTitle={
                    <CategoryBrowseViewMore
                        title={bucketTitle}
                        queryOverload={props.queryOverload}
                    >
                        <h2 className="eds-text-hs">{bucketTitle}</h2>
                    </CategoryBrowseViewMore>
                }
                lazilyRenderOffScreenElements={props.lazyLoadCarouselElements}
            >
                {events?.map((event) => {
                    return (
                        <div
                            key={event.id}
                            style={{
                                width: '97%',
                                paddingTop: '12px',
                            }}
                        >
                            <EventCard
                                gaCategory="category-page"
                                heapLocationString="CategoryBrowse - All"
                                gaOnClickLabel={bucketTitle.toString()}
                                isAuthenticated={isAuthenticated}
                                currencyFormat={currencyFormat}
                                shouldConvertToUserTimezone={true}
                                eventData={event}
                                isTargetBlank={true}
                                type={'grid'}
                                isLazyImage={true}
                                affCode={'ebdssbcategorybrowse'}
                            />
                        </div>
                    );
                })}
            </SimpleCarousel>
        </CategoryBrowseBucket>
    );
};
